import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';

export interface CarouselProps {
  children: ReactNode;
  label: string;
  previousLabel: string;
  nextLabel: string;
  className?: string;
}

const SCROLL_TOLERANCE = 1;

/**
 * Scrolls a row of arbitrary items horizontally, with snapping and navigation
 * arrows. The carousel has no say over what its items look like or how wide
 * they are — it measures the first item to work out how far to scroll.
 *
 * @example
 * ```tsx
 * <Carousel label="Articles" previousLabel="Previous" nextLabel="Next">
 *   {articles.map((article) => <ArticleCard key={article.url} {...article} />)}
 * </Carousel>
 * ```
 */
export const Carousel = ({
  children,
  label,
  previousLabel,
  nextLabel,
  className,
}: CarouselProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const { scrollLeft, scrollWidth, clientWidth } = viewport;
    const maxScrollLeft = scrollWidth - clientWidth;

    setCanScrollBack(scrollLeft > SCROLL_TOLERANCE);
    setCanScrollForward(scrollLeft < maxScrollLeft - SCROLL_TOLERANCE);
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const observer = new ResizeObserver(measure);
    observer.observe(viewport);

    return () => observer.disconnect();
  }, [measure, children]);

  const scrollByItem = (direction: -1 | 1) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.scrollBy({
      left: direction * itemStep(viewport),
      behavior: 'smooth',
    });
  };

  const hasArrows = canScrollBack || canScrollForward;

  return (
    <div className={twMerge('space-y-m', className)}>
      <div
        ref={viewportRef}
        onScroll={measure}
        role="region"
        aria-label={label}
        // A scrollable region has to be reachable by keyboard (WCAG 2.1.1);
        // browsers do not focus overflow containers consistently on their own.
        // eslint-disable-next-line jsx-a11y-x/no-noninteractive-tabindex
        tabIndex={0}
        className="flex snap-x snap-mandatory items-stretch gap-m overflow-x-auto overscroll-x-contain scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0 [&>*]:snap-start"
      >
        {children}
      </div>

      {hasArrows && (
        <div className="flex gap-xs">
          <IconButton
            intent="secondary"
            size="small"
            aria-label={previousLabel}
            disabled={!canScrollBack}
            onClick={() => scrollByItem(-1)}
          >
            <Icon name="chevron_left" color="text-content-subdued" />
          </IconButton>
          <IconButton
            intent="secondary"
            size="small"
            aria-label={nextLabel}
            disabled={!canScrollForward}
            onClick={() => scrollByItem(1)}
          >
            <Icon name="chevron_right" color="text-content-subdued" />
          </IconButton>
        </div>
      )}
    </div>
  );
};

/**
 * One item plus the gap between items, so a click lands the next item flush
 * against the start of the viewport where scroll snapping wants it.
 */
const itemStep = (viewport: HTMLElement) => {
  const firstItem = viewport.firstElementChild;
  if (!firstItem) return viewport.clientWidth;

  const gap = parseFloat(getComputedStyle(viewport).columnGap) || 0;

  return (firstItem as HTMLElement).offsetWidth + gap;
};
